<template>
  <div>
    <app-header title=""></app-header>

    <div class="container my-5">
      <div class="row mb-3">
        <div class="col d-flex align-items-center">
          <h2 class="mb-0">{{ application.name }}</h2>
        </div>
      </div>

      <div class="card mb-4">
        <div class="card-body">
          <div class="row">
            <div class="col-md-4 mb-3">
              <label class="control-label">Client</label>
              <multiselect
                :show-labels="false"
                :options="clients"
                :value="findOption(clients, application.client_id)"
                @input="application.client_id = $event ? $event.id : null"
                label="text"
                track-by="id"
                placeholder=""
              ></multiselect>
            </div>
            <div class="col-md-4 mb-3">
              <label class="control-label">Project</label>
              <multiselect
                :show-labels="false"
                :options="projects"
                :value="findOption(projects, application.project_id)"
                @input="application.project_id = $event ? $event.id : null"
                label="text"
                track-by="id"
                placeholder=""
              ></multiselect>
            </div>
            <div class="col-md-4 mb-3">
              <label class="control-label">PI Proposal ID</label>
              <input type="text" class="form-control" v-model="application.pi_proposal_id" />
            </div>
          </div>

          <h3 class="mt-3">Clusters</h3>
          <div class="row">
            <div class="col-md-4 mb-3">
              <label class="control-label">Production</label>
              <multiselect
                :show-labels="false"
                :options="productionClusters"
                :value="findOption(productionClusters, application.cluster_id)"
                @input="application.cluster_id = $event ? $event.id : null"
                label="text"
                track-by="id"
                placeholder="Stack"
              ></multiselect>
            </div>
            <div class="col-md-4 mb-3">
              <label class="control-label">QA</label>
              <multiselect
                :show-labels="false"
                :options="qaClusters"
                :value="findOption(qaClusters, application.qa_cluster_id)"
                @input="application.qa_cluster_id = $event ? $event.id : null"
                label="text"
                track-by="id"
                placeholder="QA Stack"
              ></multiselect>
            </div>
          </div>

          <h3 class="mt-3">Git Details</h3>
          <div class="row">
            <div class="col-md-4 mb-3">
              <label class="control-label">Git URL</label>
              <input type="text" class="form-control" v-model="application.git_url" />
            </div>
            <div class="col-md-4 mb-3">
              <label class="control-label">GitLab ID</label>
              <input type="text" class="form-control" v-model="application.gitlab_id" />
            </div>
          </div>

          <h3 class="mt-3">RDS Credentials</h3>
          <div class="row">
            <div class="col-md-4 mb-3">
              <label class="control-label">Username</label>
              <input type="text" class="form-control" v-model="application.terraform_rds_username" />
            </div>
            <div class="col-md-4 mb-3">
              <label class="control-label">Password</label>
              <input type="text" class="form-control" v-model="application.terraform_rds_password" />
            </div>
          </div>

          <h3 class="mt-3">Application URLs</h3>
          <div class="row">
            <div class="col-md-4 mb-3">
              <label class="control-label">App URL</label>
              <div class="input-group">
                <span class="input-group-text">https://</span>
                <input type="text" class="form-control" v-model="application.app_url" />
              </div>
            </div>
            <div class="col-md-4 mb-3">
              <label class="control-label">API URL</label>
              <div class="input-group">
                <span class="input-group-text">https://</span>
                <input type="text" class="form-control" v-model="application.api_url" />
              </div>
            </div>
          </div>

          <h3 class="mt-3">ENV Variable Files <small class="text-muted">Sent to Jenkins</small></h3>
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="control-label">QA Environment File</label>
              <textarea class="form-control env-file" v-model="application.qa_env_file"></textarea>
            </div>
            <div class="col-md-6 mb-3">
              <label class="control-label">Production Environment File</label>
              <textarea class="form-control env-file" v-model="application.production_env_file"></textarea>
            </div>
          </div>

          <div class="text-end">
            <c-button @click="updateApplication" class="btn btn-rounded btn-primary">Save</c-button>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-4">
          <div class="card h-100">
            <div class="card-body">
              <h3>Billing</h3>
              <table v-if="application && application.proposal && application.proposal.services" class="table mb-0">
                <thead>
                  <tr>
                    <th>Service Name</th>
                    <th class="text-end">Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="service in application.proposal.services" :key="service.id">
                    <td>{{ service.name }}</td>
                    <td class="text-end">${{ service.price }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td class="text-end strong">Total:</td>
                    <td class="text-end strong">${{ PITotalCost | currency }}</td>
                  </tr>
                </tfoot>
              </table>
              <p v-else class="text-muted mb-0">No PI proposal linked.</p>
            </div>
          </div>
        </div>

        <div class="col-md-6 mb-4">
          <div class="card h-100">
            <div class="card-body">
              <h3>Remove Application</h3>
              <p>Removing the application will delete all local information about the application. It will not delete the underlying infrastructure.</p>
              <div class="text-end">
                <c-button @click="destroyApplication" class="btn btn-rounded btn-danger">Remove Application</c-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Application from './../../models/application.js'
import { notify } from 'utils'
import { find } from 'lodash'

export default {
  data () {
    return {
      application: new Application(),
      clients: [],
      projects: [],
      clusters: []
    }
  },

  created () {
    this.fetchData()
  },

  computed: {
    productionClusters () {
      return this.clusters.filter(cluster => cluster.production)
    },

    qaClusters () {
      return this.clusters.filter(cluster => !cluster.production)
    },

    PITotalCost () {
      let total = 0

      if (this.application && this.application.proposal && this.application.proposal.services) {
        this.application.proposal.services.forEach((service) => {
          total += parseFloat(service.price)
        })
      }

      return total
    }
  },

  methods: {
    fetchData () {
      return Promise.all([
        client({ path: '/applications/' + this.$route.params.application_id }),
        client({ path: '/clients/list' }),
        client({ path: '/projects/list' }),
        client({ path: '/clusters/list' })
      ]).then(([application, clients, projects, clusters]) => {
        this.application = application.entity
        this.clients = clients.entity
        this.projects = projects.entity
        this.clusters = clusters.entity
      })
    },

    findOption (options, id) {
      return find(options, option => option.id == id) || null
    },

    updateApplication () {
      const data = this.application
      delete data.proposal

      if (data.pi_proposal_id == '') data.pi_proposal_id = null

      return client({ path: '/applications/' + this.$route.params.application_id, entity: data, method: 'PUT' }).then(
        () => {
          notify.success('Application updated, run Jenkins Build to apply any changes')
        },
        (response) => {
          alert('Jenkins Response Qa: ' + response['qa'].status.code + ' - ' + response['qa'].status.text)
          alert('Jenkins Response Production: ' + response['production'].status.code + ' - ' + response['production'].status.text)
        }
      )
    },

    destroyApplication () {
      return this.$swal({
        title: 'Confirm Delete',
        html: 'Are you sure you want to delete application ' + this.application.name + '?',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Delete'
      }).then(() => {
        return client({ path: '/applications/' + this.$route.params.application_id, entity: this.application, method: 'DELETE' }).then(
          () => {
            this.$router.push({ name: 'applications.index' })
          },
          (response) => {
            alert('Jenkins Response: ' + response.status.code + ' - ' + response.status.text)
          }
        )
      }, () => {})
    }
  }
}
</script>

<style scoped>
.env-file {
  height: 200px;
}
</style>
